import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {transformDataForReport} from 'nutshell-core/json-to-report-data';

import {
    updateSiteAnalyticsReportTableData,
    updateSiteAnalyticsReportChartData,
    updateAnalyticsSummaryStats,
    failSiteAnalyticsReportChartData,
} from './analytics-actions';
import type {SiteAnalyticsReportDataRequestedAction} from './analytics-actions';
import {getSiteAnalyticsReportPostParams} from './analytics-utils';
import type {SiteAnalyticsReportPostParams} from './analytics-utils';

export const requestSiteAnalyticsReportDataEpic = (action$: ActionsObservable<any>) =>
    action$.ofType('SITE_ANALYTICS_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: SiteAnalyticsReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getSiteAnalyticsReportPostParams(reportParams, filters);

            const requestStream = Observable.from(fetchData(params));
            const currentResultStream = requestStream.pipe(map((res) => res[0]));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportTableDataStream = reportDataStream.pipe(
                map(({tableData}) => updateSiteAnalyticsReportTableData(tableData))
            );

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateSiteAnalyticsReportChartData({chartData}))
            );

            const summaryStatStream = currentResultStream.pipe(
                map((currentResult) => updateAnalyticsSummaryStats(currentResult.meta))
            );

            return reportChartDataStream.pipe(
                merge(summaryStatStream.pipe(merge(reportTableDataStream))),
                catchError((err) => Observable.of(failSiteAnalyticsReportChartData(err)))
            );
        })
    );

function fetchData(params: SiteAnalyticsReportPostParams) {
    return Promise.all([fetchSiteAnalyticsReportData(params)]);
}

function fetchSiteAnalyticsReportData(params: SiteAnalyticsReportPostParams): Promise<any> {
    const url = '/rest/mcfxvisits/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    } as const;

    return $.ajax(ajaxParams) as any; // This is how we have to convince flow that the response is a ReportsResponse
}
